import { AxiosResponse } from 'axios';
import { useAlert, useAuth } from 'core/contexts';
import {
    withHeaders,
    instance as axiosInstance,
} from 'core/util/axiosInstance';
import { getDefaultHeaders } from 'core/util/other';
import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryResult,
    UseMutationResult,
} from 'react-query';
import { commonShowAlert, EdgeDefinition } from 'lib';

const fetchEdgeDefinition = async (
    url: string,
    token: string
): Promise<AxiosResponse<EdgeDefinition, any>> => {
    const headers = getDefaultHeaders(token);
    const response = await axiosInstance().get(url, withHeaders(headers));
    return response.data;
};

export const useEdgeDefinition = (
    props: any
): UseQueryResult<AxiosResponse<EdgeDefinition, any>, unknown> => {
    const { getToken } = useAuth();
    const { id } = props;
    const url = `/metadata/edge/${id}`;
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useQuery(
        ['edgeDefinition', `${id}`],
        async () => fetchEdgeDefinition(url, getToken()),
        {
            onError: (error: any) => {
                const errorMessage: string =
                    error?.data?.message ??
                    'Unable to retrieve edge definition';
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'error',
                    errorMessage,
                    JSON.stringify(error?.data ?? ''),
                    showErrorDetails
                );
            },
        }
    );
};

interface MutateEdgeDefinitionProps {
    edgeDefinition: any;
    token: string;
}
interface DeleteEdgeDictionaryProps {
    edgeId: string;
}
interface UpdateEdgeDefinitionProps {
    edgeId: string;
    condition: any;
}
const mutateEdgeDefinition = (
    props: MutateEdgeDefinitionProps
): Promise<AxiosResponse<MutateEdgeDefinitionProps, any>> => {
    const { edgeDefinition, token } = props;
    const headers = getDefaultHeaders(token);
    const url = `/metadata/edge`;

    return axiosInstance().post(url, edgeDefinition, withHeaders(headers));
};
const deleteEdgeDictionary = async (
    props: DeleteEdgeDictionaryProps,
    token: string
): Promise<AxiosResponse<void, any>> => {
    const headers = getDefaultHeaders(token);
    const url = `/metadata/edge/${props.edgeId}`;

    return axiosInstance().delete(url, withHeaders(headers));
};
const updateEdgeDefinition = (
    props: UpdateEdgeDefinitionProps,
    token: string
): Promise<AxiosResponse<MutateEdgeDefinitionProps, any>> => {
    const { edgeId, condition } = props;
    const headers = getDefaultHeaders(token);
    const url = `/metadata/edge/${edgeId}`;

    return axiosInstance().put(url, condition, withHeaders(headers));
};
export const useMutateEdgeDefinition = (): UseMutationResult<
    AxiosResponse<any, any>,
    Error,
    MutateEdgeDefinitionProps,
    unknown
> => {
    const queryClient = useQueryClient();
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(mutateEdgeDefinition, {
        onSuccess: () => {
            queryClient.invalidateQueries('edge definitions');
            commonShowAlert(showAlert, hideAlert, 'success', 'Edge Saved');
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Edge Not Saved';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};
export const useDeleteEdgeDictionary = (): UseMutationResult<
    AxiosResponse<void, any>,
    Error,
    DeleteEdgeDictionaryProps,
    unknown
> => {
    const queryClient = useQueryClient();
    const { getToken } = useAuth();
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(
        (props: DeleteEdgeDictionaryProps) =>
            deleteEdgeDictionary(props, getToken()),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('dictionaries');
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'success',
                    'Edge Dictionary Deleted'
                );
            },
            onError: (error: any) => {
                const errorMessage: string =
                    error?.data?.message ?? 'Edge Dictionary Deletion Failed';
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'error',
                    errorMessage,
                    JSON.stringify(error?.data ?? ''),
                    showErrorDetails
                );
            },
        }
    );
};

export const useUpdateEdgeDefinition = (): UseMutationResult<
    AxiosResponse<any, any>,
    Error,
    UpdateEdgeDefinitionProps,
    unknown
> => {
    const queryClient = useQueryClient();
    const { showAlert, hideAlert, showErrorDetails } = useAlert();
    const { getToken } = useAuth();

    return useMutation(
        (props: UpdateEdgeDefinitionProps) =>
            updateEdgeDefinition(props, getToken()),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('edge definitions');
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'success',
                    'Edge Definition Updated'
                );
            },
            onError: (error: any) => {
                const errorMessage: string =
                    error?.data?.message ?? 'Edge Definition Update Failed';
                commonShowAlert(
                    showAlert,
                    hideAlert,
                    'error',
                    errorMessage,
                    JSON.stringify(error?.data ?? ''),
                    showErrorDetails
                );
            },
        }
    );
};
